// Sicurezza personale dell'admin: status 2FA, disable, rigenera recovery, change password.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api';

export function SecurityPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forceChange = searchParams.get('force_change') === '1';

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const totpQuery = useQuery({ queryKey: ['totp', 'status'], queryFn: () => api.totpStatus() });

  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[] | null>(null);

  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSuccess, setPwdSuccess] = useState(false);

  useEffect(() => {
    if (forceChange) {
      // Scroll alla sezione cambio password
      const el = document.getElementById('change-password-section');
      el?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [forceChange]);

  const disableMutation = useMutation({
    mutationFn: (code: string) => api.totpDisable(code),
    onSuccess: () => {
      setShowDisableModal(false);
      setVerifyCode('');
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['totp', 'status'] });
    },
    onError: (err) => {
      setVerifyError(err instanceof ApiClientError ? err.payload.error : 'Errore');
    },
  });

  const regenMutation = useMutation({
    mutationFn: (code: string) => api.totpRegenerateRecovery(code),
    onSuccess: (data) => {
      setNewRecoveryCodes(data.recovery_codes);
      setShowRegenModal(false);
      setVerifyCode('');
      qc.invalidateQueries({ queryKey: ['totp', 'status'] });
    },
    onError: (err) => {
      setVerifyError(err instanceof ApiClientError ? err.payload.error : 'Errore');
    },
  });

  const changePwdMutation = useMutation({
    mutationFn: () =>
      api.changePassword({
        current_password: pwdCurrent,
        new_password: pwdNew,
        confirm_password: pwdConfirm,
      }),
    onSuccess: () => {
      setPwdCurrent('');
      setPwdNew('');
      setPwdConfirm('');
      setPwdError(null);
      setPwdSuccess(true);
      qc.invalidateQueries({ queryKey: ['me'] });
      if (forceChange) {
        setTimeout(() => navigate('/', { replace: true }), 1500);
      }
    },
    onError: (err) => {
      setPwdError(err instanceof ApiClientError ? err.payload.error : 'Errore');
    },
  });

  const totpEnabled = totpQuery.data?.totp_enabled ?? false;
  const mustSetup = totpQuery.data?.must_setup_totp ?? false;
  const graceUntil = totpQuery.data?.grace_until;
  const remaining = totpQuery.data?.recovery_codes_remaining ?? 0;
  const role = meQuery.data?.user.role;

  return (
    <div className="space-y-6">
      {/* Status 2FA */}
      <div
        className="rounded-lg border p-6"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <div className="mb-4 mono text-xs" style={{ color: 'var(--mute)' }}>
          Verifica in due passaggi
        </div>

        {totpEnabled ? (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-2xl">✓</span>
              <h2 className="serif text-2xl" style={{ color: 'var(--primary)' }}>
                Attivo
              </h2>
            </div>
            <p className="mb-3 text-sm" style={{ color: 'var(--mute)' }}>
              Configurato il{' '}
              {totpQuery.data?.totp_setup_at
                ? new Date(totpQuery.data.totp_setup_at).toLocaleDateString('it-IT')
                : 'recentemente'}{' '}
              · {remaining} codici di recupero rimanenti
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setShowRegenModal(true);
                  setVerifyError(null);
                  setVerifyCode('');
                }}
                className="rounded-md border px-3 py-1.5 text-sm"
                style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
              >
                Rigenera codici di recupero
              </button>
              <button
                onClick={() => {
                  setShowDisableModal(true);
                  setVerifyError(null);
                  setVerifyCode('');
                }}
                className="rounded-md border px-3 py-1.5 text-sm"
                style={{
                  borderColor: 'color-mix(in oklab, var(--danger) 40%, transparent)',
                  color: 'var(--danger)',
                }}
              >
                Disabilita 2FA
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-2xl">⚠</span>
              <h2 className="serif text-2xl" style={{ color: 'var(--ink)' }}>
                Non attivo
              </h2>
            </div>
            {mustSetup && graceUntil ? (
              <p className="mb-3 text-sm" style={{ color: 'var(--accent)' }}>
                Come admin master, devi configurare il 2FA entro il{' '}
                {new Date(graceUntil).toLocaleDateString('it-IT')}.
              </p>
            ) : (
              <p className="mb-3 text-sm" style={{ color: 'var(--mute)' }}>
                Il 2FA aggiunge un livello di protezione: anche se qualcuno scopre la tua password,
                non potrà accedere senza il codice dell'app authenticator.
              </p>
            )}
            <button
              onClick={() => navigate('/settings/2fa-setup')}
              className="rounded-md px-4 py-2 text-sm font-medium shadow-sm"
              style={{ background: 'var(--primary)', color: '#fff' }}
            >
              Configura 2FA
            </button>
          </div>
        )}
      </div>

      {/* Cambio password */}
      <div
        id="change-password-section"
        className="rounded-lg border p-6"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <div className="mb-4 mono text-xs" style={{ color: 'var(--mute)' }}>
          Password
        </div>
        <h2 className="serif mb-3 text-2xl" style={{ color: 'var(--ink)' }}>
          Cambia password
        </h2>
        {forceChange && (
          <div
            className="mb-4 rounded-md border-l-4 p-3 text-sm"
            style={{
              borderColor: 'var(--accent)',
              background: 'color-mix(in oklab, var(--accent) 12%, transparent)',
              color: 'var(--ink)',
            }}
          >
            La tua password è temporanea: cambiala ora per continuare.
          </div>
        )}
        {pwdSuccess && (
          <div
            className="mb-4 rounded-md border-l-4 p-3 text-sm"
            style={{
              borderColor: 'var(--primary)',
              background: 'color-mix(in oklab, var(--primary) 10%, transparent)',
              color: 'var(--ink)',
            }}
          >
            Password aggiornata.
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPwdError(null);
            setPwdSuccess(false);
            if (pwdNew.length < 12) {
              setPwdError('La nuova password deve essere lunga almeno 12 caratteri');
              return;
            }
            if (pwdNew !== pwdConfirm) {
              setPwdError('La conferma password non corrisponde');
              return;
            }
            changePwdMutation.mutate();
          }}
          className="space-y-3 max-w-md"
        >
          <input
            type="password"
            placeholder="Password attuale"
            autoComplete="current-password"
            value={pwdCurrent}
            onChange={(e) => setPwdCurrent(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)', color: 'var(--ink)' }}
          />
          <input
            type="password"
            placeholder="Nuova password (min. 12 caratteri)"
            autoComplete="new-password"
            value={pwdNew}
            onChange={(e) => setPwdNew(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)', color: 'var(--ink)' }}
          />
          <input
            type="password"
            placeholder="Conferma nuova password"
            autoComplete="new-password"
            value={pwdConfirm}
            onChange={(e) => setPwdConfirm(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)', color: 'var(--ink)' }}
          />
          {pwdError && (
            <p className="text-sm" style={{ color: 'var(--danger)' }}>
              {pwdError}
            </p>
          )}
          <button
            type="submit"
            disabled={!pwdCurrent || !pwdNew || !pwdConfirm || changePwdMutation.isPending}
            className="rounded-md px-4 py-2 text-sm font-medium shadow-sm disabled:opacity-50"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            {changePwdMutation.isPending ? 'Cambio…' : 'Cambia password'}
          </button>
        </form>
      </div>

      {/* Modal disabilita 2FA */}
      {showDisableModal && (
        <Modal title="Disabilita 2FA" onClose={() => setShowDisableModal(false)}>
          <p className="mb-3 text-sm" style={{ color: 'var(--ink)' }}>
            Per disabilitare il 2FA inserisci il codice corrente dall'app authenticator.
          </p>
          {role === 'master' && (
            <p className="mb-3 text-xs" style={{ color: 'var(--mute)' }}>
              Sei master: se sei l'unico master con 2FA attivo, l'operazione verrà bloccata.
            </p>
          )}
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
            className="mono mb-3 w-32 rounded-md border px-3 py-2 text-center text-xl tracking-widest"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
            placeholder="000000"
          />
          {verifyError && (
            <p className="mb-3 text-sm" style={{ color: 'var(--danger)' }}>
              {verifyError}
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => disableMutation.mutate(verifyCode)}
              disabled={verifyCode.length !== 6 || disableMutation.isPending}
              className="rounded-md px-3 py-1.5 text-sm font-medium shadow-sm disabled:opacity-50"
              style={{ background: 'var(--danger)', color: '#fff' }}
            >
              Disabilita
            </button>
            <button
              onClick={() => setShowDisableModal(false)}
              className="rounded-md border px-3 py-1.5 text-sm"
              style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
            >
              Annulla
            </button>
          </div>
        </Modal>
      )}

      {/* Modal rigenera recovery */}
      {showRegenModal && (
        <Modal title="Rigenera codici di recupero" onClose={() => setShowRegenModal(false)}>
          <p className="mb-3 text-sm" style={{ color: 'var(--ink)' }}>
            Generiamo 8 nuovi codici. Quelli vecchi saranno invalidati. Inserisci il codice corrente
            dall'app authenticator.
          </p>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
            className="mono mb-3 w-32 rounded-md border px-3 py-2 text-center text-xl tracking-widest"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
            placeholder="000000"
          />
          {verifyError && (
            <p className="mb-3 text-sm" style={{ color: 'var(--danger)' }}>
              {verifyError}
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => regenMutation.mutate(verifyCode)}
              disabled={verifyCode.length !== 6 || regenMutation.isPending}
              className="rounded-md px-3 py-1.5 text-sm font-medium shadow-sm disabled:opacity-50"
              style={{ background: 'var(--primary)', color: '#fff' }}
            >
              Rigenera
            </button>
            <button
              onClick={() => setShowRegenModal(false)}
              className="rounded-md border px-3 py-1.5 text-sm"
              style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
            >
              Annulla
            </button>
          </div>
        </Modal>
      )}

      {/* Modal nuovi recovery */}
      {newRecoveryCodes && (
        <Modal title="Nuovi codici di recupero" onClose={() => setNewRecoveryCodes(null)}>
          <p className="mb-3 text-sm" style={{ color: 'var(--ink)' }}>
            Salvali ora. I codici vecchi sono invalidati.
          </p>
          <div
            className="grid grid-cols-2 gap-1 rounded-md border p-3 mono text-sm"
            style={{ borderColor: 'var(--line)', background: 'var(--soft)' }}
          >
            {newRecoveryCodes.map((code) => (
              <div key={code}>{code}</div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => navigator.clipboard.writeText(newRecoveryCodes.join('\n'))}
              className="rounded-md border px-3 py-1.5 text-xs"
              style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
            >
              Copia tutti
            </button>
            <button
              onClick={() => setNewRecoveryCodes(null)}
              className="rounded-md px-3 py-1.5 text-xs font-medium"
              style={{ background: 'var(--primary)', color: '#fff' }}
            >
              Ho salvato i codici
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border p-6 shadow-lg"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="serif mb-4 text-xl" style={{ color: 'var(--ink)' }}>
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}
